import React, { useMemo, useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { articles, insightCategories } from '../../data/insights';
import { images } from '../../data/images';
import { PageHero } from '../../components/ui/PageHero';
import { ArticleCard } from '../../components/insights/ArticleCard';
import { NewsletterForm } from '../../components/forms/NewsletterForm';
import { Button } from '../../components/ui/Button';
export function Insights() {
  usePageMeta('Insights & Resources', 'Original perspectives on executive leadership, recruitment strategy, board governance, succession and the talent market.');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const featured = articles.find(a => a.featured) ?? articles[0];
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter(a => (!category || a.category === category) && (!q || `${a.title} ${a.excerpt} ${a.category}`.toLowerCase().includes(q)));
  }, [query, category]);
  return <>
      <PageHero crumbs={[{
      label: 'Insights'
    }]} kicker="Insights and resources" title="Perspectives on leadership and talent." intro="Original thinking for boards, executives and professionals navigating leadership decisions and career moves." data-id="element-983" data-mp="3agxtv9hz9l2e" />

      <section className="bg-ivory py-20" data-id="element-984" data-mp="20wbdxt2aoox">
        <div className="container-page" data-id="element-985" data-mp="1ztjpnnx0i7y">
          <ArticleCard article={featured} variant="feature" image={images.architecture} data-id="element-986" data-mp="3u56tu5privcg" />
        </div>
      </section>

      <section className="bg-white py-20" data-id="element-987" data-mp="3p5kglw9xpf3n">
        <div className="container-page" data-id="element-988" data-mp="3p8ghnth2wjwb">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-989" data-mp="3k8bytwsp7d2x">
            <h2 className="font-display text-4xl font-semibold text-ink" data-id="element-990" data-mp="2drqigub2osc7">Article directory</h2>
            <div className="relative w-full lg:w-96" data-id="element-991" data-mp="28rubcm72wksf">
              <label htmlFor="insight-search" className="sr-only" data-id="element-992" data-mp="28ugmswx3bkfx">
                Search articles
              </label>
              <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-993" data-mp="2f8nfy43revdc" />
              <input id="insight-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search articles" className="h-11 w-full rounded-md border border-line bg-white pl-10 pr-3 text-[15px] focus:border-midnight focus:outline-none focus:ring-2 focus:ring-gold/40" data-id="element-994" data-mp="217tb1vfx7cjf" />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2" aria-label="Filter by category" data-id="element-995" data-mp="1igjyfdaus7nm">
            {['', ...insightCategories].map(c => <button key={c || 'all'} type="button" aria-pressed={category === c} onClick={() => setCategory(c)} className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors duration-150 ${category === c ? 'border-midnight bg-midnight text-white' : 'border-line text-ink hover:border-midnight'}`} data-id="element-996" data-mp="18jxgoxdtpwpt">
                {c || 'All topics'}
              </button>)}
          </div>
          <p className="mt-6 text-sm text-muted" aria-live="polite" data-id="element-997" data-mp="yv09vrs1j9oo">
            {results.length} {results.length === 1 ? 'article' : 'articles'}
          </p>
          {results.length === 0 ? <div className="mt-6 rounded-lg border border-dashed border-line px-6 py-14 text-center" data-id="element-998" data-mp="17wq7xggfh6vf">
              <p className="font-display text-2xl text-ink" data-id="element-999" data-mp="1mle1xiaml8j9">No articles match your search.</p>
              <Button variant="outline" className="mt-5" onClick={() => {
            setQuery('');
            setCategory('');
          }} data-id="element-1000" data-mp="1hnkw4qd8igji">
                Reset filters
              </Button>
            </div> : <div className="mt-8 grid gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-3" data-id="element-1001" data-mp="3l3pmcrgas80y">
              {results.map(a => <ArticleCard key={a.slug} article={a} data-id="element-1002" data-mp="3utslzahxck89" />)}
            </div>}
        </div>
      </section>

      <section className="bg-midnight py-20 text-white" data-id="element-1003" data-mp="3nr9qym83kl1">
        <div className="container-page grid gap-10 lg:grid-cols-12 lg:items-center" data-id="element-1004" data-mp="3panahghvhj4">
          <div className="lg:col-span-5" data-id="element-1005" data-mp="3277wygox8k7v">
            <h2 className="font-display text-4xl font-semibold" data-id="element-1006" data-mp="373ol3aw4eyky">Receive new insights by email.</h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7" data-id="element-1007" data-mp="3c287fcemmcdu">
            <NewsletterForm tone="dark" data-id="element-1008" data-mp="3c5nlcbwkdfdo" />
          </div>
        </div>
      </section>
    </>;
}